// @ts-nocheck
// Simulation tab — sandbox for the admin to "play" with the invitation flow
// without burning real readers. Five sections:
//
// 1. Test recipients — localStorage list of safe-to-spam addresses (your
//    secondary, wife's email, etc.) so a stray scenario never hits a friend.
// 2. Inspector — pick any invitation; see token, status, age, magic-link URL.
// 3. Force conditions — buttons to unclaim, age by N hours, delete.
// 4. Quick scenarios — pre-canned actions (fresh invite + copy link,
//    cross-device link pair, "what-the-user-sees" preview, etc.).
// 5. Step log — chronological record of every action taken in this session.
//
// All RPCs are admin-only (gated server-side by is_current_user_admin). The
// log is React state and clears on tab switch — it's a scratchpad, not audit.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";

type Invitation = {
  id: string;
  token: string;
  email: string;
  name: string;
  created_at: string;
  claimed_at: string | null;
  claimed_by_user_id: string | null;
  kind?: "reviewer" | "gift";
};

type LogEntry = {
  at: string; // ISO
  kind: "info" | "ok" | "err";
  text: string;
};

const ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://eiforall.com";

const RECIPIENTS_KEY = "eif_sim_recipients";
const DEFAULT_RECIPIENTS: string[] = [];

function loadRecipients(): string[] {
  try {
    const raw = window.localStorage.getItem(RECIPIENTS_KEY);
    if (!raw) return DEFAULT_RECIPIENTS;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULT_RECIPIENTS;
    return arr.filter((s) => typeof s === "string" && s.includes("@"));
  } catch {
    return DEFAULT_RECIPIENTS;
  }
}

function saveRecipients(list: string[]) {
  try {
    window.localStorage.setItem(RECIPIENTS_KEY, JSON.stringify(list));
  } catch {}
}

function fmtAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) {
    const mins = Math.floor(ms / 60_000);
    return `${mins} דק'`;
  }
  if (hours < 48) return `${hours} ש'`;
  const days = Math.floor(hours / 24);
  return `${days} ימים`;
}

export function AdminSimulation() {
  const [rows, setRows] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);

  const [selectedId, setSelectedId] = useState<string>("");
  const [log, setLog] = useState<LogEntry[]>([]);

  const [recipients, setRecipients] = useState<string[]>(() => loadRecipients());
  const [newRecipient, setNewRecipient] = useState("");

  const [createName, setCreateName] = useState("בדיקה");
  const [createEmail, setCreateEmail] = useState("");
  const [ageHours, setAgeHours] = useState("2");

  // Wipe-my-progress flow: open the modal first, require the user to type a
  // confirmation phrase, then call the RPC. Destructive — no undo.
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [wipeBusy, setWipeBusy] = useState(false);

  // Load invitations whenever refresh ticks
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("reviewer_invitations")
        .select(
          "id, token, email, name, created_at, claimed_at, claimed_by_user_id, kind"
        )
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) {
        setError(error.message);
      } else {
        setRows(data || []);
        setError("");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId]
  );

  function appendLog(kind: LogEntry["kind"], text: string) {
    setLog((l) => [
      { at: new Date().toISOString(), kind, text },
      ...l,
    ].slice(0, 100));
  }

  async function doCreate(name: string, email: string) {
    if (!email || !email.includes("@")) {
      appendLog("err", "כתובת מייל לא תקינה");
      return;
    }
    appendLog("info", `יוצר הזמנה: ${name} <${email}>`);
    const { data, error } = await supabase.rpc("admin_create_invitation", {
      _name: name,
      _email: email,
      _letter: null,
    });
    if (error) {
      appendLog("err", `שגיאה: ${error.message}`);
      return;
    }
    const personal = data?.[0]?.personal_link || data?.personal_link;
    appendLog("ok", `נוצר. קישור: ${personal}`);
    setRefresh((n) => n + 1);
  }

  async function doUnclaim(id: string) {
    appendLog("info", `מחזיר ל"פתוח" את ההזמנה ${id.slice(0, 8)}…`);
    const { error } = await supabase.rpc("admin_unclaim_invitation", { _id: id });
    if (error) {
      appendLog("err", `שגיאה: ${error.message}`);
      return;
    }
    appendLog("ok", "ההזמנה אופסה למצב פתוח");
    setRefresh((n) => n + 1);
  }

  async function doAge(id: string, hours: number) {
    appendLog("info", `מתיישן את ההזמנה ${id.slice(0, 8)} ב-${hours} שעות`);
    const { error } = await supabase.rpc("admin_age_invitation", {
      _id: id,
      _hours: hours,
    });
    if (error) {
      appendLog("err", `שגיאה: ${error.message}`);
      return;
    }
    appendLog("ok", `תאריך יצירה הוזז אחורה ב-${hours} שעות`);
    setRefresh((n) => n + 1);
  }

  async function doDelete(id: string) {
    if (!window.confirm("למחוק את ההזמנה?")) return;
    appendLog("info", `מוחק ${id.slice(0, 8)}`);
    const { error } = await supabase.rpc("admin_delete_invitation", { _id: id });
    if (error) {
      appendLog("err", `שגיאה: ${error.message}`);
      return;
    }
    appendLog("ok", "נמחק");
    setSelectedId("");
    setRefresh((n) => n + 1);
  }

  async function copyToClipboard(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      appendLog("ok", `הועתק: ${label}`);
    } catch {
      appendLog("err", "ההעתקה נכשלה — העתק ידנית");
    }
  }

  // Wipe ALL of the calling admin's reader-side data — every chapter, every
  // writing, every ruler/colour tag. Server-side delete via the
  // admin_wipe_my_progress RPC (migration 0020), plus local cleanup of the
  // known ei_* localStorage keys so the next page load starts truly clean.
  // Destructive — gated by the confirm-text modal above.
  async function doWipeMyProgress() {
    setWipeBusy(true);
    appendLog("info", "מוחק את כל נתוני הקריאה שלי…");
    const { data, error } = await supabase.rpc("admin_wipe_my_progress");
    if (error) {
      setWipeBusy(false);
      appendLog("err", `שגיאה: ${error.message}`);
      return;
    }
    // Clear local caches so reload starts from the (now empty) cloud state.
    try {
      window.localStorage.removeItem("ei_book_pos");
      window.localStorage.removeItem("ei_book_ch1");
    } catch {}
    appendLog("ok", `נמחקו ${data || 0} רשומות התקדמות מהשרת. הדף ייטען מחדש כדי להציג מצב ריק.`);
    setWipeBusy(false);
    setWipeOpen(false);
    setWipeConfirmText("");
    window.setTimeout(() => window.location.reload(), 800);
  }

  function addRecipient() {
    const e = newRecipient.trim();
    if (!e || !e.includes("@")) return;
    if (recipients.includes(e)) return;
    const next = [...recipients, e];
    setRecipients(next);
    saveRecipients(next);
    setNewRecipient("");
    appendLog("info", `נוסף נמען בדיקה: ${e}`);
  }

  function removeRecipient(e: string) {
    const next = recipients.filter((x) => x !== e);
    setRecipients(next);
    saveRecipients(next);
    appendLog("info", `הוסר נמען בדיקה: ${e}`);
  }

  return (
    <div className="adm-sim">
      <style dangerouslySetInnerHTML={{ __html: simStyles }} />

      {error && <div className="adm-error">{error}</div>}

      {/* ===== Section 1: Test recipients ===== */}
      <section className="sim-section">
        <h3>נמעני בדיקה</h3>
        <p className="sim-hint">
          רשימת כתובות שאתה משתמש בהן לבדיקות, נשמרת בדפדפן הזה. כך לא תשלח
          בטעות הזמנה לקורא אמיתי תוך כדי ניסוי.
        </p>
        <div className="sim-row">
          <input
            className="adm-search"
            placeholder="הוסף כתובת בדיקה (לחיצה על Enter)"
            value={newRecipient}
            onChange={(e) => setNewRecipient(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addRecipient()}
            dir="ltr"
            style={{ maxWidth: 360 }}
          />
          <button className="adm-btn" onClick={addRecipient}>
            + הוסף
          </button>
        </div>
        {recipients.length > 0 ? (
          <ul className="sim-recipients">
            {recipients.map((e) => (
              <li key={e}>
                <span className="mono">{e}</span>
                <button
                  className="adm-btn secondary tiny"
                  onClick={() => removeRecipient(e)}
                >
                  הסר
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="sim-empty">עוד אין נמענים. הוסף את עצמך / האישה / מייל משני.</div>
        )}
      </section>

      {/* ===== Section 2: Quick-create ===== */}
      <section className="sim-section">
        <h3>יצירת הזמנה לבדיקה</h3>
        <p className="sim-hint">
          נשלחת רק לכתובת מהרשימה למעלה. שולחת מייל אמיתי דרך Resend SMTP —
          זהו מבחן הקצה לקצה האמיתי של מערך ההזמנות.
        </p>
        <div className="sim-row sim-create">
          <input
            className="adm-search"
            placeholder="שם"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            style={{ maxWidth: 160 }}
          />
          <select
            className="adm-search"
            value={createEmail}
            onChange={(e) => setCreateEmail(e.target.value)}
            style={{ maxWidth: 280 }}
          >
            <option value="">— בחר נמען —</option>
            {recipients.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            className="adm-btn"
            disabled={!createEmail || !createName}
            onClick={() => doCreate(createName, createEmail)}
          >
            + צור הזמנה
          </button>
        </div>
      </section>

      {/* ===== Section 3: Inspector ===== */}
      <section className="sim-section">
        <h3>בוחר הזמנה</h3>
        <div className="sim-row">
          <select
            className="adm-search"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{ maxWidth: 480 }}
            disabled={loading}
          >
            <option value="">— בחר הזמנה לבחינה —</option>
            {rows.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} ({r.email}) — {r.claimed_at ? "ניצל" : "פתוח"} — {fmtAge(r.created_at)} אחורה
              </option>
            ))}
          </select>
          <button
            className="adm-btn secondary"
            onClick={() => setRefresh((n) => n + 1)}
            disabled={loading}
          >
            רענן
          </button>
        </div>

        {selected && (
          <div className="sim-inspector adm-card">
            <table className="sim-kv">
              <tbody>
                <tr><th>שם</th><td>{selected.name}</td></tr>
                <tr><th>מייל</th><td className="mono">{selected.email}</td></tr>
                <tr><th>סטטוס</th><td>
                  {selected.claimed_at ? (
                    <span className="adm-pill ok">ניצל</span>
                  ) : (
                    <span className="adm-pill pending">פתוח</span>
                  )}
                </td></tr>
                <tr><th>נוצר</th><td className="num">{new Date(selected.created_at).toLocaleString("he-IL")} ({fmtAge(selected.created_at)} אחורה)</td></tr>
                {selected.claimed_at && (
                  <tr><th>נוצל</th><td className="num">{new Date(selected.claimed_at).toLocaleString("he-IL")}</td></tr>
                )}
                <tr><th>טוקן</th><td className="mono" style={{ fontSize: 12 }}>{selected.token}</td></tr>
                <tr><th>קישור</th><td>
                  <a className="mono" href={`${ORIGIN}/r/${selected.token}`} target="_blank" rel="noreferrer">
                    {ORIGIN}/r/{selected.token}
                  </a>
                </td></tr>
              </tbody>
            </table>

            <div className="sim-row" style={{ marginTop: 12, flexWrap: "wrap" }}>
              <button
                className="adm-btn secondary"
                onClick={() => copyToClipboard(`${ORIGIN}/r/${selected.token}`, "קישור אישי")}
              >
                העתק קישור
              </button>
              <a
                className="adm-btn secondary"
                href={`${ORIGIN}/r/${selected.token}`}
                target="_blank"
                rel="noreferrer"
              >
                פתח בלשונית חדשה
              </a>
              {selected.claimed_at && (
                <button
                  className="adm-btn"
                  onClick={() => doUnclaim(selected.id)}
                  title="מאפס את claimed_at — מאפשר תביעה מחדש"
                >
                  ↺ החזר לפתוח
                </button>
              )}
              <span className="sim-divider" />
              <label className="sim-inline-label">יישן ב-</label>
              <input
                className="adm-search"
                type="number"
                min="0"
                max="2400"
                value={ageHours}
                onChange={(e) => setAgeHours(e.target.value)}
                style={{ maxWidth: 80 }}
              />
              <span className="sim-inline-label">שעות</span>
              <button
                className="adm-btn secondary"
                onClick={() => doAge(selected.id, parseInt(ageHours, 10) || 0)}
                title="מזיז את created_at אחורה — סימולציה של הזמנה ישנה"
              >
                ⏪ יישן
              </button>
              <span className="sim-divider" />
              <button
                className="adm-btn danger"
                onClick={() => doDelete(selected.id)}
              >
                מחק
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ===== Section 4: Pre-canned scenarios ===== */}
      <section className="sim-section">
        <h3>תרחישים מהירים</h3>
        <p className="sim-hint">
          לחיצה אחת מבצעת רצף פעולות. שני התרחישים הראשונים דורשים נמען
          בחור ממסכי "נמעני בדיקה" + "יצירת הזמנה" למעלה.
        </p>
        <div className="sim-scenarios">
          <Scenario
            title="💌 שליחה רגילה"
            desc="יצירת הזמנה חדשה לנמען הנבחר → קישור מועתק ללוח."
            disabled={!createEmail || !createName}
            onClick={async () => {
              await doCreate(createName, createEmail);
            }}
          />
          <Scenario
            title="🕐 הזמנה ישנה (24 שעות)"
            desc="יצירה + ייאון מיידי. בודק האם הקישור עדיין עובד אחרי 24 שעות."
            disabled={!createEmail || !createName}
            onClick={async () => {
              await doCreate(createName, createEmail);
              setTimeout(async () => {
                const fresh = rows[0];
                if (fresh) await doAge(fresh.id, 24);
              }, 800);
            }}
          />
          <Scenario
            title="🔁 שחזור נוצל → פתוח"
            desc="עובד על ההזמנה הנבחרת. רק אם היא במצב ניצל."
            disabled={!selected || !selected.claimed_at}
            onClick={async () => {
              if (selected) await doUnclaim(selected.id);
            }}
          />
          <Scenario
            title="📋 העתק קישור של הזמנה נבחרת"
            desc="להדבקה ב-WhatsApp / Slack / לכל מקום."
            disabled={!selected}
            onClick={async () => {
              if (selected) await copyToClipboard(`${ORIGIN}/r/${selected.token}`, "קישור אישי");
            }}
          />
        </div>
      </section>

      {/* ===== Section 5: Step log ===== */}
      <section className="sim-section">
        <h3>יומן פעולות (עד 100 אחרונות)</h3>
        {log.length === 0 ? (
          <div className="sim-empty">היומן יתמלא כשתתחיל לנסות פעולות.</div>
        ) : (
          <ul className="sim-log">
            {log.map((e, i) => (
              <li key={i} className={`sim-log-${e.kind}`}>
                <span className="sim-log-time">{e.at.slice(11, 19)}</span>
                <span className="sim-log-text">{e.text}</span>
              </li>
            ))}
          </ul>
        )}
        {log.length > 0 && (
          <button
            className="adm-btn secondary tiny"
            onClick={() => setLog([])}
            style={{ marginTop: 8 }}
          >
            נקה יומן
          </button>
        )}
      </section>

      {/* ===== Section 6: Danger zone — wipe my own reader data ===== */}
      <section className="sim-section" style={{ border: "1.5px solid #E88086", background: "#FFF5F6" }}>
        <h3 style={{ color: "#A0383E" }}>אזור מסוכן — אפס את הנתונים שלי</h3>
        <p className="sim-hint">
          מוחק את כל מה שכתבת בכל פרקי הספר — תכונות, צבעים, סרגלים, וכל
          תרגיל פתוח. נשמע פשוט, אבל אין undo. נועד לעצמך, כדי שתוכל לבצע
          סבב בדיקה נקי על הספר מבלי שייווצרו לך כפילויות עם הניסויים הקודמים.
          <br />
          <strong>אינו מוחק:</strong> את החשבון, את ההרשאות שלך, את ההזמנות
          שיצרת לקוראים אחרים, את הרכישות.
        </p>
        <button
          className="adm-btn danger"
          onClick={() => setWipeOpen(true)}
        >
          🗑️ מחק את כל הכתיבה שלי בספר
        </button>
      </section>

      {wipeOpen && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget && !wipeBusy) setWipeOpen(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000, padding: 16 }}
        >
          <div style={{ background: "#FFFDF9", borderRadius: 14, padding: "22px 24px", maxWidth: 460, width: "100%", boxShadow: "0 18px 48px rgba(0,0,0,0.28)" }}>
            <h3 style={{ margin: 0, color: "#A0383E", fontSize: 18 }}>אישור: מחיקת כל הכתיבה שלך</h3>
            <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.7, color: "var(--deep)" }}>
              פעולה זו תמחק לצמיתות את כל מה שכתבת בכל הפרקים: תכונות, צבעים,
              סרגלים, תרגילים פתוחים, ומיקום קריאה אחרון. הנתונים יימחקו גם
              מהענן וגם מהדפדפן הזה. אין undo.
            </p>
            <p style={{ marginTop: 8, fontSize: 14, color: "var(--deep)" }}>
              כדי לאשר, הקלידו את המשפט הבא: <strong>מחק הכל</strong>
            </p>
            <input
              type="text"
              value={wipeConfirmText}
              onChange={(e) => setWipeConfirmText(e.target.value)}
              placeholder="הקלידו: מחק הכל"
              disabled={wipeBusy}
              autoFocus
              dir="rtl"
              style={{ width: "100%", padding: "10px 12px", border: "1.5px solid var(--sand)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", textAlign: "right", direction: "rtl", marginTop: 6 }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-start" }}>
              <button
                className="adm-btn danger"
                disabled={wipeConfirmText.trim() !== "מחק הכל" || wipeBusy}
                onClick={doWipeMyProgress}
              >
                {wipeBusy ? "מוחק…" : "כן, מחק את כל הכתיבה שלי"}
              </button>
              <button
                className="adm-btn secondary"
                onClick={() => { setWipeOpen(false); setWipeConfirmText(""); }}
                disabled={wipeBusy}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Scenario({
  title,
  desc,
  disabled,
  onClick,
}: {
  title: string;
  desc: string;
  disabled: boolean;
  onClick: () => void | Promise<void>;
}) {
  return (
    <button
      className="sim-scenario"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "חסר תנאי — קרא את ההסבר" : ""}
    >
      <div className="sim-scenario-title">{title}</div>
      <div className="sim-scenario-desc">{desc}</div>
    </button>
  );
}

const simStyles = `
.adm-sim { display: flex; flex-direction: column; gap: 18px; }
.sim-section { background: #fff; border-radius: 12px; padding: 16px 18px; box-shadow: 0 1px 2px rgba(0,0,0,.04); }
.sim-section h3 { margin: 0 0 6px 0; font-size: 17px; }
.sim-hint { color: #6B5E50; font-size: 13px; margin: 0 0 12px 0; line-height: 1.5; }
.sim-empty { color: #8C7E70; font-size: 13px; padding: 8px 0; }
.sim-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sim-create { gap: 10px; }
.sim-recipients { list-style: none; padding: 0; margin: 10px 0 0 0; display: flex; flex-direction: column; gap: 4px; }
.sim-recipients li { display: flex; align-items: center; justify-content: space-between; padding: 6px 10px; background: #F8F2E8; border-radius: 6px; }
.sim-inspector { margin-top: 12px; padding: 14px; }
.sim-kv { width: 100%; border-collapse: collapse; }
.sim-kv th { text-align: right; vertical-align: top; padding: 6px 12px 6px 0; color: #6B5E50; font-weight: 500; font-size: 13px; width: 80px; }
.sim-kv td { padding: 6px 0; font-size: 14px; word-break: break-all; }
.sim-divider { display: inline-block; width: 1px; height: 24px; background: #E0D5C5; margin: 0 4px; }
.sim-inline-label { color: #6B5E50; font-size: 13px; }
.sim-scenarios { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; }
.sim-scenario { text-align: right; padding: 12px 14px; background: #F8F2E8; border: 1px solid #E0D5C5; border-radius: 10px; cursor: pointer; transition: background .15s; }
.sim-scenario:hover:not(:disabled) { background: #F0E5D4; }
.sim-scenario:disabled { opacity: .45; cursor: not-allowed; }
.sim-scenario-title { font-weight: 600; font-size: 14px; margin-bottom: 4px; }
.sim-scenario-desc { color: #6B5E50; font-size: 12px; line-height: 1.4; }
.sim-log { list-style: none; padding: 0; margin: 0; max-height: 240px; overflow-y: auto; border: 1px solid #E0D5C5; border-radius: 8px; }
.sim-log li { display: flex; gap: 10px; padding: 6px 12px; font-size: 13px; border-bottom: 1px solid #F0E5D4; }
.sim-log li:last-child { border-bottom: none; }
.sim-log-time { color: #8C7E70; font-family: monospace; font-size: 12px; min-width: 60px; }
.sim-log-text { flex: 1; word-break: break-word; }
.sim-log-ok { background: #F0F8F0; }
.sim-log-err { background: #FCEBEC; color: #A0383E; }
.sim-log-info { background: #fff; }
.adm-btn.danger { background: #F0A0A6; color: #6E0010; }
.adm-btn.danger:hover { background: #E88086; }
`;
